export type ChatImage = { filename: string; url: string };
/** A PDF sent as a file to models that read PDFs; `text` is its text layer as fallback. */
export type ChatFile = { filename: string; url: string; text: string };
export type AttachmentPreview = {
  filename: string;
  text: string;
  image?: ChatImage;
  file?: ChatFile;
  /** Pages of a scanned PDF, rendered as images for vision models. */
  images?: ChatImage[];
};

import type { DocumentProcessingStatus } from './lib/documentProcessing';
/**
 * Typed wrapper around fetch for the chat backend. Reads the CSRF cookie
 * (set by the server on login) and echoes it as `x-csrf-token` on every
 * non-GET request — matching the double-submit pattern enforced by the
 * backend's CSRF middleware.
 */

export type ApiUser = {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  /** Profile photo from the Microsoft sign-in; initials are shown without one. */
  photoUrl?: string | null;
  role: 'admin' | 'member';
  agentsEnabled: boolean;
  preferredLanguage: 'nl' | 'en' | 'fr';
};

export type Product = {
  profile: 'full' | 'light';
  /** Where the gateway admin console lives (not the chat's own /admin page). */
  adminUrl?: string;
  capabilities: {
    modelSelection: boolean;
    knowledgeSelection: boolean;
    skills: boolean;
    projects: boolean;
    artifacts: boolean;
    automations: boolean;
    agents: boolean;
    advancedAdmin: boolean;
    externalIntegrations: boolean;
  };
};

export type Model = {
  id: string;
  object: 'model';
  created: number;
  owned_by: string;
  scoreIcons?: {
    performance: 'bolt' | 'star' | 'coin' | 'circle' | 'diamond';
    quality: 'bolt' | 'star' | 'coin' | 'circle' | 'diamond';
    cost: 'bolt' | 'star' | 'coin' | 'circle' | 'diamond';
  };
  scores?: { performance: number | null; quality: number | null; cost: number | null };
};

export type DatasetOwner = {
  name: string;
  email: string | null;
  phone: string | null;
  photoUrl: string | null;
};

export type DatasetGroup = {
  slug: string;
  name: string;
  description: string | null;
  owner: DatasetOwner | null;
  datasets: Array<{
    slug: string;
    name: string;
    description: string | null;
    parameterSchema: unknown;
  }>;
};

export type SkillDraft = {
  name: string;
  description: string;
  body: string;
  outputType?: 'text' | 'interactive_html' | 'table' | 'todo';
  datasetGroupSlugs?: string[];
};
export type SkillSharingGroup = { id: string; name: string };

export type Skill = {
  personal?: {
    canEdit: boolean;
    sharedGroupId: string | null;
    version: number;
    /** Only present once the gateway stores it (see skills report). */
    allowTeamEdits?: boolean;
  };
  /** Phrases that trigger the skill; only present once the gateway stores them. */
  examples?: string[];
  id: string;
  teamId: string;
  slug: string;
  name: string;
  description: string;
  domain: string;
  effort: string;
  priority?: number;
  tags: string[];
  model: string | null;
  datasetGroupSlugs: string[];
  outputType?: 'text' | 'interactive_html' | 'table' | 'todo';
  inputs: { name: string; type: string }[];
  body: string;
  updatedAt: string;
};

export type ConversationListItem = {
  id: string;
  title: string;
  modelId: string;
  projectId: string | null;
  selectedSkillSlug: string | null;
  selectedSkillSlugs: string[];
  enabledDatasetGroups: string[];
  updatedAt: string;
  messageCount: number;
};

export type StoredMessage = {
  id: string;
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: {
    text?: string;
    images?: ChatImage[];
    tool_calls?: Array<{
      id: string;
      type: 'function';
      function: { name: string; arguments: string };
    }>;
    tool_call_id?: string;
    output?: unknown;
  };
  tokensIn: number | null;
  tokensOut: number | null;
  finishReason: string | null;
  latencyMs: number | null;
  datasetGroupSlugs: string[];
  /** Model "nivio": the orchestrator's plan and step progress of this answer. */
  orchestration?: unknown[] | null;
  /** Thumbs on a NIVIO answer: 1, -1 or null. */
  feedback?: number | null;
  /** The model that wrote this answer; absent or null for older answers. */
  modelId?: string | null;
  /** Duet thread: what this card points at; absent or null for chat messages. */
  card?: DuetCard | null;
  seenAt?: string | null;
  createdAt: string;
};

export type DuetCard =
  | {
      type: 'result';
      runId: string;
      conversationId: string | null;
      title: string;
      kind: 'agent' | 'scheduled_task';
      status: 'completed' | 'error';
      preset?: string;
      /** Tokens the run used, when known. */
      tokens?: number;
      /** The follow-ups (by number) the user tapped. */
      done?: number[];
    }
  | { type: 'approval'; approvalId: string; title: string; actionKind: string; toolName: string }
  | {
      type: 'alert';
      itemKey: string;
      severity: 'attention' | 'urgent';
      title: string;
      done?: number[];
    };

export type DuetMonitor = {
  enabled: boolean;
  enabledDatasetGroups: string[];
  maxPushesPerDay: number;
  /** Items that need attention push too, not only urgent ones. */
  pushAttention: boolean;
  allHours: boolean;
  /** The user's own rules in plain words. */
  rules: string;
};

export type NiviPreferences = {
  /** Switched on in Settings; until then Nivi is not in the menu. */
  enabled: boolean;
  defaultTab: 'chat' | 'duet';
  /** Local `HH:MM`; both null when quiet hours are off. */
  quietStart: string | null;
  quietEnd: string | null;
};

/** What the user did with a monitor alert: muted, snoozed or thumbs. */
export type DuetItemState = {
  itemKey: string;
  muted: boolean;
  snoozedUntil: string | null;
  feedback: 1 | -1 | null;
};

export type DuetBrief = {
  enabled: boolean;
  time: string;
  weekdaysOnly: boolean;
  enabledDatasetGroups: string[];
};

export type DuetSummary = {
  conversationId: string | null;
  /** Result cards not seen yet. */
  unseen: number;
  pendingApprovals: number;
  defaultTab: 'chat' | 'duet';
  enabled: boolean;
};

export type ConversationDetail = {
  id: string;
  /** The server is still making an answer for this conversation. */
  generating?: boolean;
  version: number;
  title: string;
  modelId: string;
  projectId: string | null;
  systemPrompt: string | null;
  selectedSkillSlug: string | null;
  selectedSkillSlugs: string[];
  /** Skill the chat picked by itself for the latest turn (`teamId/slug`). */
  autoSkillKey?: string | null;
  enabledDatasetGroups: string[];
  createdAt: string;
  updatedAt: string;
  messages: StoredMessage[];
};

export type ConversationTools = {
  sqliteDatabases: Array<{
    id: string;
    filename: string;
  }>;
};

export type Automation = {
  id: string;
  kind: 'scheduled_task' | 'agent';
  title: string;
  skillSlug: string;
  prompt: string;
  modelId: string;
  enabledDatasetGroups: string[];
  everyMinutes: number;
  scheduleTime: string | null;
  timeZone: string | null;
  status: 'enabled' | 'paused';
  notifyOnFinish: boolean;
  /** A daily schedule that skips Saturday and Sunday. */
  weekdaysOnly: boolean;
  /** Agents: success rate (0-100) of the last builder test; null when never tested. */
  lastTestScore: number | null;
  nextRunAt: string;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AgentTest = {
  name: string;
  scenario: string;
  checks: Array<{ text: string; important: boolean }>;
};

/** What the agent builder's writer proposes; nothing is saved yet. */
export type AgentDraft = {
  title: string;
  instructions: string;
  skillKey: string;
  datasetGroups: string[];
  modelId: string;
  schedule: { kind: 'manual' | 'weekdays' | 'daily' | 'weekly' | 'hourly'; time: string | null };
  tests: AgentTest[];
};

export type AgentTestResult = {
  answer: string;
  /** The answer up to the length the next team member reads. */
  output: string;
  checks: Array<{ text: string; important: boolean; passed: boolean; note: string }>;
};

export type AgentSuggestion = { title: string; find: string; replace: string };

/** One specialist of a proposed team: one of the user's agents, or a new one (agentId null). */
export type TeamMemberDraft = {
  agentId: string | null;
  title: string;
  instructions: string;
  skillKey: string;
  datasetGroups: string[];
  modelId: string;
  checks: Array<{ text: string; important: boolean }>;
};

/** What the team builder's writer proposes; nothing is saved yet. */
export type TeamDraft = {
  title: string;
  brief: string;
  members: TeamMemberDraft[];
  coordinatorChecks: Array<{ text: string; important: boolean }>;
  schedule: AgentDraft['schedule'];
};

type TeamFields = {
  title: string;
  prompt: string;
  everyMinutes: number;
  scheduleTime: string | null;
  timeZone: string | null;
  weekdaysOnly: boolean;
  lastTestScore?: number;
  /** Null: the standard NIVIO coordinator. */
  coordinatorAgentId: string | null;
  specialistAgentIds: string[];
};

export type AutomationRun = {
  id: string;
  automationId: string;
  conversationId: string | null;
  status: 'queued' | 'running' | 'completed' | 'error';
  resultPreview: string | null;
  error: string | null;
  startedAt: string;
  completedAt: string | null;
  seenAt: string | null;
};

/** A run that finished after the poll cursor, for the "task finished" notification. */
export type FinishedRun = {
  id: string;
  automationId: string;
  title: string;
  conversationId: string | null;
  status: 'completed' | 'error';
  completedAt: string;
};

export type AgentActionApproval = {
  id: string;
  automationId: string;
  teamRunId: string | null;
  conversationId: string | null;
  toolName: string;
  actionKind: 'send' | 'write' | 'delete';
  datasetGroupSlug: string | null;
  arguments: unknown;
  status:
    | 'pending'
    | 'approved'
    | 'executing'
    | 'executed'
    | 'rejected'
    | 'expired'
    | 'error'
    | 'unknown';
  error: string | null;
  createdAt: string;
  expiresAt: string;
  approvedAt: string | null;
  executionStartedAt: string | null;
  completedAt: string | null;
  verifiedAt: string | null;
};

/** One proposal with the name of the agent that made it. */
export type AgentActionApprovalDetail = AgentActionApproval & { agentTitle: string };

export type AgentTeam = {
  id: string;
  title: string;
  prompt: string;
  everyMinutes: number;
  scheduleTime: string | null;
  weekdaysOnly: boolean;
  /** Success rate (0-100) of the last team builder test; null when never tested. */
  lastTestScore: number | null;
  status: 'enabled' | 'paused';
  nextRunAt: string;
  lastRunAt: string | null;
  members: Array<{
    agentId: string;
    title: string;
    role: 'coordinator' | 'specialist';
    position: number;
  }>;
};

export type AgentTeamRun = {
  id: string;
  teamId: string;
  conversationId: string | null;
  status: 'running' | 'completed' | 'error';
  resultPreview: string | null;
  error: string | null;
  startedAt: string;
  completedAt: string | null;
  handoffs: Array<{
    id: string;
    fromAgentId: string | null;
    toAgentId: string;
    position: number;
    status: 'running' | 'completed' | 'error';
    conversationId: string | null;
    output: string | null;
    error: string | null;
  }>;
};

/** This month's usage per model (gateway audit log), for the account menu. */
/** The models behind Chat's plain choices (null = not offered) and whether all models show. */
export type ModelChoice = {
  fast: string | null;
  balanced: string | null;
  thorough: string | null;
  showAllModels: boolean;
};

export type UsageBreakdown = {
  budgetUsd: number | null;
  spentUsd: number;
  models: Array<{
    model: string;
    requests: number;
    tokensIn: number;
    tokensOut: number;
    costUsd: number;
  }>;
};

export type UsageSummary = {
  allTime: { messages: number; tokensIn: number; tokensOut: number };
  currentMonth: { messages: number; tokensIn: number; tokensOut: number };
};

export type Project = {
  id: string;
  name: string;
  description: string | null;
  instructions: string | null;
  defaultDatasetGroups: string[];
  /** 'shared' when shared with everyone or with at least one group. */
  visibility: 'private' | 'shared';
  sharedWithEveryone: boolean;
  /** The groups it is shared with; only filled in for the owner. */
  sharedGroupIds: string[];
  /** Skills switched on by default in each new conversation of the project. */
  skillSlugs: string[];
  /** The user owns the project; others may use it but not change it. */
  isOwner: boolean;
  /** Who shared the project; null for the owner. */
  ownerName: string | null;
  /** Present on the project list only. */
  documentCount?: number;
  createdAt: string;
  updatedAt: string;
};

export type ProjectDocument = {
  id: string;
  projectId: string;
  filename: string;
  displayName: string | null;
  description: string | null;
  tags: string[];
  active: boolean;
  version: number;
  mimeType: string;
  sizeBytes: number;
  checksumSha256: string;
  status: string;
  errorMessage: string | null;
  indexedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ProjectDetail = Project & { documents: ProjectDocument[] };

export type ProjectDocumentPreview = {
  id: string;
  filename: string;
  displayName: string | null;
  status: string;
  text: string;
  truncated: boolean;
};

export type ProjectPolicy = {
  maxUploadBytes: number;
  maxDocuments: number;
  maxStorageBytes: number;
  allowedExtensions: string[] | null;
  ocrEnabled: boolean;
};

export type ToolFailure = {
  messageId: string;
  conversationId: string;
  conversationTitle: string;
  modelId: string;
  userEmail: string;
  toolCallId: string | null;
  toolName: string | null;
  error: unknown;
  createdAt: string;
};

export type MicrosoftMailStatus = {
  configured: boolean;
  delegatedEnabled: boolean;
  appOnlyEnabled: boolean;
  appOnlyAllowedMailboxes: string[];
  delegatedConnected: boolean;
  delegatedEmail: string | null;
  delegatedScopes: string[];
};

export type AzureDevOpsConnection = {
  configured: boolean;
  enabled: boolean;
  organization: string | null;
  personalAccessTokenAllowed: boolean;
  personalAccessToken: { accountName: string | null; savedAt: string } | null;
};

export type LeexiConnection = {
  configured: boolean;
  enabled: boolean;
  personalKeysAllowed: boolean;
  companyKeyConfigured: boolean;
  personalKey: { label: string | null; savedAt: string } | null;
};

export type MicrosoftConnectionStatus = MicrosoftMailStatus & {
  connected: boolean;
  /** The connection lacks permissions added later (mail content, calendar); connect again. */
  reconnectRequired?: boolean;
  accountEmail: string | null;
  scopes: string[];
  capabilities: {
    mail: {
      configured: boolean;
      delegatedEnabled: boolean;
      appOnlyEnabled: boolean;
      delegatedConnected: boolean;
      delegatedEmail: string | null;
      delegatedScopes: string[];
    };
    sharePoint: {
      delegatedConnected: boolean;
      delegatedScopes: string[];
    };
    oneDrive: {
      delegatedConnected: boolean;
      delegatedScopes: string[];
    };
    teams: {
      delegatedConnected: boolean;
      delegatedScopes: string[];
    };
  };
};

/** The user's own record of the Chat welcome tour; null until they finish or skip it. */
export type OnboardingRecord = {
  tour: 'chat';
  version: number;
  status: 'completed' | 'skipped';
  updatedAt: string;
};

export type MicrosoftMailSendDraft = {
  to: string[];
  cc: string[];
  subject: string;
  bodyText?: string;
  bodyHtml?: string;
};

class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

function readCookie(name: string): string | null {
  const target = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(target)) return trimmed.slice(target.length);
  }
  return null;
}

function readCsrfCookie(): string | null {
  return __PRODUCT_PROFILE__ === 'light'
    ? readCookie('__Host-ncl_csrf')
    : (readCookie('__Host-nc_csrf') ?? readCookie('nc_csrf'));
}

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') {
    const csrf = readCsrfCookie();
    if (csrf) headers['x-csrf-token'] = csrf;
  }
  const init: RequestInit = { method, headers, credentials: 'include', signal: signal ?? null };
  if (body !== undefined) init.body = JSON.stringify(body);
  const res = await fetch(path, init);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let message = text || `request failed (${res.status})`;
    try {
      const parsed = JSON.parse(text) as { error?: { message?: string } };
      if (parsed.error?.message) message = parsed.error.message;
    } catch {
      // body wasn't JSON
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Message of an upload a proxy refused for its size; pages show their own text for it. */
export const UPLOAD_TOO_LARGE = 'upload_too_large';

async function uploadRequest<T>(path: string, form: FormData, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  const csrf = readCsrfCookie();
  if (csrf) headers['x-csrf-token'] = csrf;
  const res = await fetch(path, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: form,
    signal: signal ?? null,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    // A proxy or the server cut off a too large upload before the app could answer
    // ("413", "502 Bad Gateway" as HTML): say what happened instead.
    if ([413, 502, 504].includes(res.status) && !text.trim().startsWith('{')) {
      throw new ApiError(res.status, UPLOAD_TOO_LARGE);
    }
    let message = text || `request failed (${res.status})`;
    try {
      const parsed = JSON.parse(text) as { error?: { message?: string } };
      if (parsed.error?.message) message = parsed.error.message;
    } catch {
      // body wasn't JSON
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as T;
}

export const api = {
  async login(
    email: string,
    password: string,
  ): Promise<{ user: ApiUser & { allowedModels: string[]; allowedDatasetGroups: string[] } }> {
    return request('POST', '/api/auth/login', { email, password });
  },
  async ssoConfig(): Promise<{ enabled: boolean }> {
    return request('GET', '/api/auth/sso/config');
  },
  async logout(): Promise<void> {
    await request('POST', '/api/auth/logout', {});
  },
  async duetSummary(): Promise<DuetSummary> {
    return request('GET', '/api/duet');
  },
  async openDuet(modelId: string): Promise<{ conversationId: string }> {
    return request('POST', '/api/duet/open', { modelId });
  },
  async markDuetSeen(): Promise<void> {
    await request('POST', '/api/duet/seen', {});
  },
  async getDuetBrief(): Promise<{ data: DuetBrief | null }> {
    return request('GET', '/api/duet/brief');
  },
  async saveDuetBrief(
    input: DuetBrief & { modelId: string; timeZone: string },
  ): Promise<{ data: DuetBrief | null }> {
    return request('PATCH', '/api/duet/brief', input);
  },
  async getDuetMonitor(): Promise<{ data: DuetMonitor | null }> {
    return request('GET', '/api/duet/monitor');
  },
  async saveDuetMonitor(
    input: DuetMonitor & { modelId: string; timeZone: string },
  ): Promise<{ data: DuetMonitor | null }> {
    return request('PATCH', '/api/duet/monitor', input);
  },
  /** A follow-up under a Nivi card was tapped; the card shows it as done. */
  async markDuetActionDone(messageId: string, index: number): Promise<void> {
    await request('POST', '/api/duet/actions/done', { messageId, index });
  },
  /** A test push to every device; the number of devices it reached. */
  async sendTestPush(): Promise<{ devices: number }> {
    return request('POST', '/api/push/test', {});
  },
  async muteDuetItem(itemKey: string): Promise<void> {
    await request('POST', '/api/duet/items/mute', { itemKey });
  },
  async getNiviPreferences(): Promise<NiviPreferences> {
    return request('GET', '/api/duet/preferences');
  },
  async setQuietHours(input: {
    quietStart: string | null;
    quietEnd: string | null;
    timeZone: string;
  }): Promise<void> {
    await request('PATCH', '/api/duet/preferences', input);
  },
  async listDuetItems(): Promise<{ data: DuetItemState[] }> {
    return request('GET', '/api/duet/items');
  },
  async snoozeDuetItem(itemKey: string, days: 1 | 7): Promise<void> {
    await request('POST', '/api/duet/items/snooze', { itemKey, days });
  },
  async restoreDuetItem(itemKey: string): Promise<void> {
    await request('POST', '/api/duet/items/restore', { itemKey });
  },
  async setDuetItemFeedback(itemKey: string, feedback: 1 | -1 | null): Promise<void> {
    await request('POST', '/api/duet/items/feedback', { itemKey, feedback });
  },
  /** This device's push settings; `niviPushes` changes them, leaving it out only reads. */
  async pushDevice(endpoint: string, niviPushes?: boolean): Promise<{ niviPushes: boolean }> {
    return request('POST', '/api/push/device', {
      endpoint,
      ...(niviPushes === undefined ? {} : { niviPushes }),
    });
  },
  async setNiviEnabled(enabled: boolean): Promise<void> {
    await request('PATCH', '/api/duet/preferences', { enabled });
  },
  async setDuetDefaultTab(defaultTab: 'chat' | 'duet'): Promise<void> {
    await request('PATCH', '/api/duet/preferences', { defaultTab });
  },
  async pushConfig(): Promise<{ enabled: boolean; publicKey?: string }> {
    return request('GET', '/api/push/config');
  },
  async pushSubscribe(subscription: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
    userAgent?: string;
  }): Promise<void> {
    await request('POST', '/api/push/subscribe', subscription);
  },
  async pushUnsubscribe(endpoint: string): Promise<void> {
    await request('POST', '/api/push/unsubscribe', { endpoint });
  },
  async me(): Promise<{
    user: ApiUser;
    product: Product;
    /** Admin's chat attachment limits in bytes; null = no limit. */
    attachmentLimits?: { maxFileBytes: number | null; maxTotalBytes: number | null };
    /** Link to the company's AI policy (Admin › Chat settings); null = none. */
    aiPolicyUrl?: string | null;
    /** Admin's models behind the plain choices Fast, Balanced and Thorough. */
    modelChoice?: ModelChoice;
  }> {
    return request('GET', '/api/me');
  },
  async onboarding(): Promise<OnboardingRecord | null> {
    return request('GET', '/api/me/onboarding');
  },
  async recordOnboarding(input: {
    version: number;
    status: OnboardingRecord['status'];
  }): Promise<OnboardingRecord> {
    return request('POST', '/api/me/onboarding', input);
  },
  async listModels(): Promise<{ data: Model[] }> {
    return request('GET', '/api/models');
  },
  async listDatasetGroups(): Promise<{ data: DatasetGroup[] }> {
    return request('GET', '/api/dataset-groups');
  },
  async createPersonalSkill(input: SkillDraft): Promise<Skill> {
    return request('POST', '/api/personal-skills', input);
  },
  async updatePersonalSkill(
    id: string,
    input: SkillDraft & {
      sharedGroupId: string | null;
      expectedVersion: number;
      /** Sent only when the gateway reported the fields on the loaded skill. */
      examples?: string[];
      allowTeamEdits?: boolean;
    },
  ): Promise<Skill> {
    return request('PATCH', `/api/personal-skills/${encodeURIComponent(id)}`, input);
  },
  async deletePersonalSkill(id: string, expectedVersion: number): Promise<void> {
    return request(
      'DELETE',
      `/api/personal-skills/${encodeURIComponent(id)}?expectedVersion=${expectedVersion}`,
    );
  },
  /** Offers the user's own skill to the administrators for the whole organisation. */
  async proposePersonalSkill(id: string): Promise<{ status: 'pending' }> {
    return request('POST', `/api/personal-skills/${encodeURIComponent(id)}/propose`);
  },
  async skillSharingGroups(): Promise<{ data: SkillSharingGroup[] }> {
    return request('GET', '/api/skill-sharing-groups');
  },
  async branchConversation(conversationId: string, messageId: string): Promise<{ id: string }> {
    return request('POST', `/api/conversations/${conversationId}/branch`, { messageId });
  },
  async draftSkill(conversationId: string, messageId: string): Promise<SkillDraft> {
    return request('POST', `/api/conversations/${encodeURIComponent(conversationId)}/skill-draft`, {
      messageId,
    });
  },

  /** The chat model writes or revises a skill from the user's description; nothing is saved. */
  async writeSkill(input: {
    request: string;
    modelId: string;
    current?: { name: string; description: string; body: string };
  }): Promise<SkillDraft> {
    return request('POST', '/api/skill-writer', input);
  },

  async listSkills(): Promise<{ data: Skill[] }> {
    return request('GET', '/api/skills');
  },
  async skillUsage(): Promise<{ since: string; data: Record<string, number> }> {
    return request('GET', '/api/skills/usage');
  },
  async usageBreakdown(): Promise<UsageBreakdown> {
    return request('GET', '/api/me/usage-breakdown');
  },
  async usage(): Promise<UsageSummary> {
    return request('GET', '/api/usage');
  },
  async microsoftConnectionStatus(): Promise<MicrosoftConnectionStatus> {
    return request('GET', '/api/microsoft/connection/status');
  },
  async disconnectMicrosoft(): Promise<void> {
    await request('POST', '/api/microsoft/connection/disconnect', {});
  },
  async connectMicrosoft(): Promise<{ authorizationUrl: string; expiresInSeconds: number }> {
    return request('POST', '/api/microsoft/connection/connect', {});
  },
  async azureDevOpsConnection(): Promise<AzureDevOpsConnection> {
    return (await request<{ data: AzureDevOpsConnection }>('GET', '/api/azure-devops/connection'))
      .data;
  },
  async saveAzureDevOpsToken(token: string): Promise<AzureDevOpsConnection> {
    return (
      await request<{ data: AzureDevOpsConnection }>('POST', '/api/azure-devops/connection', {
        token,
      })
    ).data;
  },
  async removeAzureDevOpsToken(): Promise<AzureDevOpsConnection> {
    return (
      await request<{ data: AzureDevOpsConnection }>('DELETE', '/api/azure-devops/connection')
    ).data;
  },
  async leexiConnection(): Promise<LeexiConnection> {
    return (await request<{ data: LeexiConnection }>('GET', '/api/leexi/connection')).data;
  },
  async saveLeexiKey(key: { keyId: string; keySecret: string }): Promise<LeexiConnection> {
    return (await request<{ data: LeexiConnection }>('POST', '/api/leexi/connection', key)).data;
  },
  async removeLeexiKey(): Promise<LeexiConnection> {
    return (await request<{ data: LeexiConnection }>('DELETE', '/api/leexi/connection')).data;
  },
  async microsoftMailStatus(): Promise<MicrosoftMailStatus> {
    return request('GET', '/api/microsoft/mail/status');
  },
  async connectMicrosoftMail(): Promise<{ authorizationUrl: string; expiresInSeconds: number }> {
    return request('POST', '/api/microsoft/mail/connect', {});
  },
  async sendMicrosoftMail(draft: MicrosoftMailSendDraft): Promise<unknown> {
    return request('POST', '/api/microsoft/mail/send', draft);
  },
  async listToolFailures(): Promise<{ data: ToolFailure[] }> {
    return request('GET', '/api/admin/tool-failures');
  },
  async projectPolicy(): Promise<ProjectPolicy> {
    return request('GET', '/api/project-policy');
  },
  async listConversations(
    opts: { limit?: number; perProject?: number } = {},
  ): Promise<{ data: ConversationListItem[] }> {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.perProject) params.set('per_project', String(opts.perProject));
    const query = params.toString();
    return request('GET', `/api/conversations${query ? `?${query}` : ''}`);
  },
  async searchConversations(opts: {
    q?: string;
    projectId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: ConversationListItem[]; total: number }> {
    const params = new URLSearchParams();
    if (opts.q) params.set('q', opts.q);
    if (opts.projectId) params.set('project', opts.projectId);
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.offset) params.set('offset', String(opts.offset));
    return request('GET', `/api/conversations/search?${params.toString()}`);
  },
  async projectConversationCounts(): Promise<{ data: Record<string, number> }> {
    return request('GET', '/api/conversations/project-counts');
  },
  /** B15: file and app blocks of recent answers, for the Files page. */
  async listArtifactFences(): Promise<{
    data: Array<{
      conversationId: string;
      conversationTitle: string;
      messageId: string;
      fences: string;
    }>;
  }> {
    return request('GET', '/api/conversations/artifacts');
  },
  async getConversation(id: string): Promise<ConversationDetail> {
    return request('GET', `/api/conversations/${id}`);
  },
  async stopConversation(id: string): Promise<{ stopped: boolean }> {
    return request('POST', `/api/conversations/${id}/stop`);
  },
  /** Thumbs on a NIVIO answer: 1, -1, or 0 to take the rating back. */
  async rateAnswer(
    conversationId: string,
    messageId: string,
    rating: 1 | -1 | 0,
    reason?: 'wrong' | 'layout' | 'not_asked' | 'slow',
  ): Promise<{ rating: number | null }> {
    return request('POST', `/api/conversations/${conversationId}/messages/${messageId}/feedback`, {
      rating,
      ...(reason ? { reason } : {}),
    });
  },
  async summarizeConversation(
    id: string,
  ): Promise<{ summary: ConversationSummary; upToMessageId: string | null }> {
    return request('POST', `/api/conversations/${id}/summary`, {});
  },
  async continueConversation(
    id: string,
    body: {
      title: string;
      summary: ConversationSummary;
      labels: { from: string; topic: string; decided: string; open: string };
    },
  ): Promise<{ id: string }> {
    return request('POST', `/api/conversations/${id}/continue`, body);
  },
  async listTemplates(): Promise<{ data: ChatTemplate[] }> {
    return request('GET', '/api/templates');
  },
  async createTemplate(body: {
    name: string;
    prompt: string;
    modelId: string;
    selectedSkillSlugs: string[];
    enabledDatasetGroups: string[];
    shared: boolean;
  }): Promise<ChatTemplate> {
    return request('POST', '/api/templates', body);
  },
  async updateTemplate(
    id: string,
    body: Partial<
      Pick<
        ChatTemplate,
        | 'name'
        | 'prompt'
        | 'modelId'
        | 'selectedSkillSlugs'
        | 'enabledDatasetGroups'
        | 'shared'
        | 'hidden'
      >
    >,
  ): Promise<ChatTemplate> {
    return request('PATCH', `/api/templates/${id}`, body);
  },
  /** The order of the user's own templates, first one first. */
  async orderTemplates(ids: string[]): Promise<{ ok: true }> {
    return request('POST', '/api/templates/order', { ids });
  },
  async deleteTemplate(id: string): Promise<{ ok: true }> {
    return request('DELETE', `/api/templates/${id}`);
  },
  async getConversationTools(id: string): Promise<{ data: ConversationTools }> {
    return request('GET', `/api/conversations/${id}/tools`);
  },
  async createConversation(input: {
    modelId: string;
    title?: string;
    projectId?: string | null;
    systemPrompt?: string;
    selectedSkillSlug?: string | null;
    selectedSkillSlugs?: string[];
    enabledDatasetGroups?: string[];
  }): Promise<ConversationDetail> {
    return request('POST', '/api/conversations', input);
  },
  async patchConversation(
    id: string,
    patch: {
      expectedVersion: number;
      title?: string;
      modelId?: string;
      projectId?: string | null;
      selectedSkillSlug?: string | null;
      selectedSkillSlugs?: string[];
      enabledDatasetGroups?: string[];
      systemPrompt?: string | null;
      autoSkillOff?: boolean;
    },
  ): Promise<void> {
    await request('PATCH', `/api/conversations/${id}`, patch);
  },
  async deleteConversation(id: string): Promise<void> {
    await request('DELETE', `/api/conversations/${id}`);
  },
  async listAutomations(agentMode = false): Promise<{
    data: Automation[];
    runs: AutomationRun[];
    unread: number;
    /** Tasks that need the user: failed latest run, or a finished run to open with notifications on. */
    attention?: number;
  }> {
    return request('GET', agentMode ? '/api/agents' : '/api/automations');
  },
  async createAutomation(
    input: {
      title: string;
      skillSlug: string;
      prompt: string;
      modelId: string;
      enabledDatasetGroups: string[];
      everyMinutes: number;
      scheduleTime?: string | null;
      timeZone?: string | null;
      notifyOnFinish?: boolean;
      weekdaysOnly?: boolean;
      lastTestScore?: number;
    },
    agentMode = false,
  ): Promise<Automation> {
    return request('POST', agentMode ? '/api/agents' : '/api/automations', input);
  },
  async patchAutomation(
    id: string,
    patch: Partial<{
      title: string;
      skillSlug: string;
      prompt: string;
      modelId: string;
      enabledDatasetGroups: string[];
      everyMinutes: number;
      scheduleTime: string | null;
      timeZone: string | null;
      status: 'enabled' | 'paused';
      notifyOnFinish: boolean;
      weekdaysOnly: boolean;
      /** Only numbers are sent; null matches the agent type. */
      lastTestScore: number | null;
    }>,
    agentMode = false,
  ): Promise<void> {
    await request('PATCH', `${agentMode ? '/api/agents' : '/api/automations'}/${id}`, patch);
  },
  /** The agent builder writes (or rewrites) an agent from a description; nothing is saved. */
  async writeAgent(input: {
    request: string;
    current?: { title: string; instructions: string };
    /** The user's usual chat model, used unless the skill fixes one. */
    preferredModel?: string;
  }): Promise<AgentDraft> {
    return request('POST', '/api/agents/builder/write', input);
  },
  /** Runs one test case without write actions and judges the answer. */
  async testAgent(input: {
    draft: Pick<AgentDraft, 'instructions' | 'skillKey' | 'modelId' | 'datasetGroups'>;
    test: AgentTest;
  }): Promise<AgentTestResult> {
    return request('POST', '/api/agents/builder/test', input);
  },
  /** NIVIO proposes a team for the task, from the user's agents and new ones; nothing is saved. */
  async writeTeam(input: { request: string; preferredModel?: string }): Promise<TeamDraft> {
    return request('POST', '/api/agents/builder/team/write', input);
  },
  /** Runs one step of a team test without write actions and judges it. */
  async testTeamStep(input: {
    brief: string;
    role: 'specialist' | 'coordinator';
    title: string;
    member: Pick<TeamMemberDraft, 'instructions' | 'skillKey' | 'modelId' | 'datasetGroups'>;
    completed: Array<{ title: string; output: string }>;
    checks: Array<{ text: string; important: boolean }>;
  }): Promise<AgentTestResult> {
    return request('POST', '/api/agents/builder/team/step', input);
  },
  async improveAgent(input: {
    instructions: string;
    results: Array<{
      name: string;
      checks: Array<{ text: string; passed: boolean; note: string }>;
    }>;
  }): Promise<{ suggestions: AgentSuggestion[] }> {
    return request('POST', '/api/agents/builder/improve', input);
  },
  async deleteAutomation(id: string, agentMode = false): Promise<void> {
    await request('DELETE', `${agentMode ? '/api/agents' : '/api/automations'}/${id}`);
  },
  async runAutomation(id: string, agentMode = false): Promise<AutomationRun> {
    return request('POST', `${agentMode ? '/api/agents' : '/api/automations'}/${id}/run`, {});
  },
  async markAutomationRunSeen(id: string, agentMode = false): Promise<void> {
    await request('POST', `${agentMode ? '/api/agents' : '/api/automations'}/runs/${id}/seen`, {});
  },
  /** Runs finished after `since`; `now` is the next cursor. Without `since` the list is empty. */
  async finishedRuns(
    agentMode: boolean,
    since: string | null,
  ): Promise<{ data: FinishedRun[]; now: string }> {
    const base = agentMode ? '/api/agents' : '/api/automations';
    return request(
      'GET',
      `${base}/runs/finished${since ? `?since=${encodeURIComponent(since)}` : ''}`,
    );
  },
  async listAgentTeams(): Promise<{ data: AgentTeam[]; runs: AgentTeamRun[] }> {
    return request('GET', '/api/agent-teams');
  },
  async createAgentTeam(input: TeamFields): Promise<AgentTeam> {
    return request('POST', '/api/agent-teams', input);
  },
  async patchAgentTeam(
    id: string,
    patch: Partial<TeamFields & { status: 'enabled' | 'paused' }>,
  ): Promise<AgentTeam> {
    return request('PATCH', `/api/agent-teams/${id}`, patch);
  },
  async deleteAgentTeam(id: string): Promise<void> {
    await request('DELETE', `/api/agent-teams/${id}`);
  },
  async runAgentTeam(id: string): Promise<AgentTeamRun> {
    return request('POST', `/api/agent-teams/${id}/run`, {});
  },
  async listAgentActionApprovals(): Promise<{ data: AgentActionApproval[] }> {
    return request('GET', '/api/agent-action-approvals');
  },
  async getAgentActionApproval(id: string): Promise<AgentActionApprovalDetail> {
    return request('GET', `/api/agent-action-approvals/${encodeURIComponent(id)}`);
  },
  async approveAgentActionApproval(id: string): Promise<AgentActionApproval> {
    return request('POST', `/api/agent-action-approvals/${id}/approve`, {});
  },
  async resolveAgentActionApproval(
    id: string,
    outcome: 'executed' | 'not_executed',
  ): Promise<void> {
    await request('POST', `/api/agent-action-approvals/${id}/resolve`, { outcome });
  },
  async rejectAgentActionApproval(id: string): Promise<void> {
    await request('POST', `/api/agent-action-approvals/${id}/reject`, {});
  },
  async listProjects(): Promise<{ data: Project[] }> {
    return request('GET', '/api/projects');
  },
  async getProject(id: string): Promise<ProjectDetail> {
    return request('GET', `/api/projects/${id}`);
  },
  async createProject(input: {
    name: string;
    description?: string | null;
    instructions?: string | null;
    defaultDatasetGroups?: string[];
  }): Promise<Project> {
    return request('POST', '/api/projects', input);
  },
  async patchProject(
    id: string,
    patch: Partial<{
      name: string;
      description: string | null;
      instructions: string | null;
      defaultDatasetGroups: string[];
      sharedWithEveryone: boolean;
      sharedGroupIds: string[];
      skillSlugs: string[];
    }>,
  ): Promise<Project> {
    return request('PATCH', `/api/projects/${id}`, patch);
  },
  async deleteProject(id: string): Promise<void> {
    await request('DELETE', `/api/projects/${id}`);
  },
  async extractChatAttachment(
    id: string,
    file: File,
    signal?: AbortSignal,
  ): Promise<AttachmentPreview> {
    const form = new FormData();
    form.set('file', file);
    return uploadRequest(`/api/conversations/${id}/attachments`, form, signal);
  },
  async transcriptionStatus(): Promise<{ available: boolean }> {
    return request('GET', '/api/transcribe/status');
  },
  async transcribeAudio(
    file: File,
    language: string,
    signal?: AbortSignal,
  ): Promise<{ text: string }> {
    const form = new FormData();
    form.set('file', file);
    form.set('language', language);
    return uploadRequest<{ text: string }>('/api/transcribe', form, signal);
  },
  async speechStatus(): Promise<{ available: boolean }> {
    return request('GET', '/api/speech/status');
  },
  /** An answer as a few sentences to say aloud (the model that writes titles makes it). */
  async speechSummary(
    text: string,
    language: string,
    model: string,
    signal?: AbortSignal,
  ): Promise<string> {
    const headers: Record<string, string> = {
      accept: 'application/json',
      'content-type': 'application/json',
    };
    const csrf = readCsrfCookie();
    if (csrf) headers['x-csrf-token'] = csrf;
    const res = await fetch('/api/speech/summary', {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ text, language, model }),
      signal: signal ?? null,
    });
    if (!res.ok) throw new ApiError(res.status, `summary failed (${res.status})`);
    return ((await res.json()) as { text: string }).text;
  },
  /** One spoken clip of `input` (at most 4096 characters), in the voice the admin chose. */
  async speak(input: string, signal?: AbortSignal): Promise<Blob> {
    const headers: Record<string, string> = {
      accept: 'audio/*',
      'content-type': 'application/json',
    };
    const csrf = readCsrfCookie();
    if (csrf) headers['x-csrf-token'] = csrf;
    const res = await fetch('/api/speech', {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ input }),
      signal: signal ?? null,
    });
    if (!res.ok) throw new ApiError(res.status, `speech failed (${res.status})`);
    const blob = await res.blob();
    // Safari plays a blob URL only with a real audio type; proxies may drop or blur it.
    return blob.type.startsWith('audio/') ? blob : new Blob([blob], { type: 'audio/mpeg' });
  },
  async uploadProjectDocument(
    id: string,
    file: File,
    signal?: AbortSignal,
  ): Promise<ProjectDocument> {
    const form = new FormData();
    form.set('file', file);
    return uploadRequest<ProjectDocument>(`/api/projects/${id}/documents`, form, signal);
  },
  async reindexProjectDocument(projectId: string, documentId: string): Promise<ProjectDocument> {
    return request('POST', `/api/projects/${projectId}/documents/${documentId}/reindex`, {});
  },
  async patchProjectDocument(
    projectId: string,
    documentId: string,
    patch: Partial<{
      displayName: string | null;
      description: string | null;
      tags: string[];
      active: boolean;
    }>,
  ): Promise<ProjectDocument> {
    return request('PATCH', `/api/projects/${projectId}/documents/${documentId}`, patch);
  },
  async getProjectDocumentStatus(
    projectId: string,
    documentId: string,
    signal?: AbortSignal,
  ): Promise<DocumentProcessingStatus> {
    return request(
      'GET',
      `/api/projects/${projectId}/documents/${documentId}/status`,
      undefined,
      signal,
    );
  },
  async getProjectDocumentPreview(
    projectId: string,
    documentId: string,
    signal?: AbortSignal,
  ): Promise<ProjectDocumentPreview> {
    return request(
      'GET',
      `/api/projects/${projectId}/documents/${documentId}/preview`,
      undefined,
      signal,
    );
  },
  async deleteProjectDocument(projectId: string, documentId: string): Promise<void> {
    await request('DELETE', `/api/projects/${projectId}/documents/${documentId}`);
  },
};

export { ApiError };

export type ConversationSummary = { topic: string; decided: string[]; open: string[] };

export type ChatTemplate = {
  id: string;
  name: string;
  prompt: string;
  modelId: string;
  selectedSkillSlugs: string[];
  enabledDatasetGroups: string[];
  shared: boolean;
  /** Kept, but not shown on a new chat. */
  hidden: boolean;
  mine: boolean;
};
